import React from 'react';

interface SvgProps extends React.SVGProps<SVGSVGElement> {
  s?: number;
  w?: number;
}

const Svg: React.FC<SvgProps> = ({ s = 22, w = 1.9, children, ...rest }) => (
  <svg
    width={s}
    height={s}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={w}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {children}
  </svg>
);

export const OrganicIcons = {
  back: () => (
    <Svg s={18}>
      <path d="M15 18l-6-6 6-6" />
    </Svg>
  ),
  chev: () => (
    <Svg s={20}>
      <path d="M9 18l6-6-6-6" />
    </Svg>
  ),
  arrow: () => (
    <Svg s={20} w={2.2}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Svg>
  ),
  check: (p?: { s?: number }) => (
    <Svg s={p?.s || 15} w={2.6}>
      <path d="M20 6L9 17l-5-5" />
    </Svg>
  ),
  house: () => (
    <Svg>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-6h4v6" />
    </Svg>
  ),
  houses: () => (
    <Svg>
      <path d="M3 10l5-4 5 4v9H3z" />
      <path d="M13 19V9l4-3 4 3v10" />
    </Svg>
  ),
  build: () => (
    <Svg>
      <rect x="5" y="3" width="14" height="18" rx="1" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
    </Svg>
  ),
  bolt: () => (
    <Svg>
      <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
    </Svg>
  ),
  ware: () => (
    <Svg>
      <path d="M3 21V8l9-4 9 4v13" />
      <path d="M7 21v-7h10v7" />
      <path d="M7 17h10" />
    </Svg>
  ),
  perg: () => (
    <Svg>
      <path d="M3 7l9-3 9 3" />
      <path d="M5 7v13M19 7v13M9 7v13M15 7v13" />
      <path d="M3 11h18" />
    </Svg>
  ),
  help: () => (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9a2.8 2.8 0 015.3 1.2c0 1.9-2.8 2.3-2.8 4" />
      <path d="M12 17.5v.01" />
    </Svg>
  ),
  heat: () => (
    <Svg>
      <path d="M14 14.5V5a2 2 0 10-4 0v9.5a4 4 0 104 0z" />
      <path d="M12 9v6" />
    </Svg>
  ),
  swap: () => (
    <Svg>
      <path d="M4 8h13l-3.5-3.5M20 16H7l3.5 3.5" />
    </Svg>
  ),
  duo: () => (
    <Svg>
      <circle cx="8.5" cy="8.5" r="3" />
      <path d="M8.5 3.5v1M8.5 12.5v1M3.5 8.5h1M12.5 8.5h1" />
      <path d="M16.5 13.5V19m0-5.5a2 2 0 10-1.8 3.4M16.5 13.5a2 2 0 011.8 3.4" />
    </Svg>
  ),
  cal: () => (
    <Svg>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9.5h18M8 3v4M16 3v4" />
    </Svg>
  ),
  alarm: () => (
    <Svg>
      <circle cx="12" cy="13" r="7" />
      <path d="M12 10v3l2 2" />
      <path d="M5 4L2.5 6.5M19 4l2.5 2.5" />
    </Svg>
  ),
  lock: () => (
    <Svg s={16}>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 018 0v3" />
    </Svg>
  ),
  shield: () => (
    <Svg s={15}>
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
    </Svg>
  ),
  users: () => (
    <Svg s={15}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0112 0" />
      <path d="M16 6a3 3 0 010 6M21 20a6 6 0 00-4-5.7" />
    </Svg>
  ),
  euro: () => (
    <Svg s={15}>
      <path d="M17 8a6 6 0 100 8M4 10h7M4 14h7" />
    </Svg>
  ),
  card: () => (
    <Svg s={15}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 10h18M7 14.5h3" />
    </Svg>
  ),
  clock: () => (
    <Svg s={14}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Svg>
  ),
  sun: () => (
    <Svg s={14}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.4 1.4M17.6 17.6L19 19M19 5l-1.4 1.4M6.4 17.6L5 19" />
    </Svg>
  ),
};

export type OrganicIconName = keyof typeof OrganicIcons;

export const renderIcon = (name: string, props?: { s?: number }) => {
  const Icon = OrganicIcons[name as OrganicIconName];
  return Icon ? Icon(props) : null;
};
